import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { inr, fmtDate } from "@/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, ArrowLeft, MapPin, Plus, Landmark, Building2, Pencil, History } from "lucide-react";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const { data: project } = useQuery({
    queryKey: ["project", id],
    queryFn: async () => (await api.get(`/projects/${id}`)).data,
  });
  const { data: units = [] } = useQuery({
    queryKey: ["units", id],
    queryFn: async () => (await api.get("/units", { params: { project_id: id } })).data,
  });
  const { data: pnl } = useQuery({
    queryKey: ["pnl", id],
    queryFn: async () => (await api.get("/dashboard/pnl", { params: { project_id: id } })).data,
  });

  const bulkMut = useMutation({
    mutationFn: async (body) => (await api.post("/units/bulk", body)).data,
    onSuccess: (d) => { toast.success(`Created ${d.created} units`); qc.invalidateQueries({ queryKey: ["units", id] }); qc.invalidateQueries({ queryKey: ["project", id] }); },
  });

  const [bulk, setBulk] = useState({ tower: "A", wing: "A", floors: 6, units_per_floor: 4, unit_type: "flat", starting_area_sqft: 1050, starting_price: 8500000 });

  // Pre-fill bulk defaults from project configuration (once project loads)
  React.useEffect(() => {
    if (!project) return;
    const floorsFromProject = project.total_floors || 0;
    const towers = project.number_of_towers || 0;
    const flats = project.number_of_flats || 0;
    // If we know per-tower floors and total flats, infer units_per_floor
    let upf = 0;
    if (towers > 0 && flats > 0 && floorsFromProject > 0) {
      upf = Math.max(1, Math.round(flats / (towers * floorsFromProject)));
    }
    setBulk((b) => ({
      ...b,
      floors: floorsFromProject || b.floors,
      units_per_floor: upf || b.units_per_floor,
    }));
  }, [project]);

  async function runForecast() {
    setAiBusy(true); setAiText("");
    try {
      const { data } = await api.post(`/ai/forecast/${id}`);
      setAiText(data.forecast);
    } catch (e) {
      setAiText("Forecast unavailable right now.");
    } finally { setAiBusy(false); }
  }

  if (!project) return <div className="text-sm text-zinc-500">Loading project…</div>;

  const s = project.unit_stats || {};

  return (
    <div className="space-y-6" data-testid="project-detail">
      <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
        <ArrowLeft size={12} /> Back to projects
      </Link>

      <div className="flex justify-end">
        <Link to={`/projects/${id}/edit`}>
          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="edit-project-btn">
            <Pencil size={13} className="mr-1" /> Edit Site
          </Button>
        </Link>
      </div>

      <Card className="overflow-hidden border-zinc-200 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3">
          <div className="md:col-span-1 aspect-[16/9] md:aspect-auto bg-zinc-100">
            {project.thumbnail_url && <img src={project.thumbnail_url + (project.updated_at ? `?v=${encodeURIComponent(project.updated_at)}` : "")} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="md:col-span-2 p-6 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest font-mono bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded">{project.code}</span>
              {project.ownership_type === "jda" ? (
                <Badge className="bg-amber-500 text-white hover:bg-amber-500">JDA</Badge>
              ) : (
                <Badge className="bg-emerald-700 hover:bg-emerald-700">Company Owned</Badge>
              )}
              <span className="text-[11px] uppercase tracking-widest text-zinc-500">{project.project_type}</span>
            </div>
            <h1 className="font-heading text-3xl font-bold">{project.name}</h1>
            <div className="text-sm text-zinc-500 flex items-center gap-1"><MapPin size={13} /> {project.location}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 text-xs">
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Est. Revenue</div><div className="font-mono font-semibold mt-0.5">{inr(project.estimated_revenue)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Est. Cost</div><div className="font-mono font-semibold mt-0.5">{inr(project.estimated_cost)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Land Area</div><div className="font-mono font-semibold mt-0.5">{project.land_area_sqft} sqft</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">RERA</div><div className="font-mono font-semibold mt-0.5">{project.rera_number || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Start</div><div className="font-mono font-semibold mt-0.5">{fmtDate(project.start_date)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Completion</div><div className="font-mono font-semibold mt-0.5">{fmtDate(project.completion_date)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Manager</div><div className="font-mono font-semibold mt-0.5">{project.project_manager || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Architect</div><div className="font-mono font-semibold mt-0.5">{project.architect || "—"}</div></div>
            </div>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-zinc-200">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="units" data-testid="tab-units">Inventory ({s.total || 0})</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance">Finance</TabsTrigger>
          <TabsTrigger value="jda" data-testid="tab-jda">JDA</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <ProjectConfigCard project={project} unitStats={s} />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Revenue</div><div className="kpi-num mt-1">{inr(pnl?.total_revenue)}</div></CardContent></Card>
            <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Expense</div><div className="kpi-num mt-1">{inr(pnl?.total_expense)}</div></CardContent></Card>
            <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Gross Profit</div><div className="kpi-num mt-1">{inr(pnl?.gross_profit)}</div></CardContent></Card>
            <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Booked Value</div><div className="kpi-num mt-1">{inr(pnl?.sold_units_value)}</div></CardContent></Card>
          </div>

          <Card className="bg-white border-zinc-200">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="font-heading text-base flex items-center gap-2"><Sparkles size={15} className="text-emerald-700" /> AI Project Forecast</CardTitle>
              <Button size="sm" onClick={runForecast} disabled={aiBusy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="ai-forecast-btn">
                {aiBusy ? "Analyzing…" : "Run Forecast"}
              </Button>
            </CardHeader>
            <CardContent>
              {aiText ? (
                <pre className="text-sm whitespace-pre-wrap font-body text-zinc-800">{aiText}</pre>
              ) : (
                <div className="text-sm text-zinc-500">Click <b>Run Forecast</b> to have GPT 5.6 Terra project bookings, revenue and risks based on live data.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="units" className="mt-4 space-y-4">
          <Card className="bg-white border-zinc-200">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="font-heading text-base flex items-center gap-2"><Building2 size={16} /> Units — {s.total || 0}</CardTitle>
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid="bulk-units-btn"><Plus size={14} className="mr-1" /> Bulk Create Tower</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-heading">Bulk create a tower</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Tower</Label><Input value={bulk.tower} onChange={(e) => setBulk({ ...bulk, tower: e.target.value })} /></div>
                    <div><Label>Wing</Label><Input value={bulk.wing} onChange={(e) => setBulk({ ...bulk, wing: e.target.value })} /></div>
                    <div><Label>Floors</Label><Input type="number" value={bulk.floors} onChange={(e) => setBulk({ ...bulk, floors: +e.target.value })} /></div>
                    <div><Label>Units / floor</Label><Input type="number" value={bulk.units_per_floor} onChange={(e) => setBulk({ ...bulk, units_per_floor: +e.target.value })} /></div>
                    <div><Label>Unit Type</Label>
                      <Select value={bulk.unit_type} onValueChange={(v) => setBulk({ ...bulk, unit_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flat">Flat</SelectItem>
                          <SelectItem value="row_house">Row House</SelectItem>
                          <SelectItem value="shop">Shop</SelectItem>
                          <SelectItem value="office">Office</SelectItem>
                          <SelectItem value="showroom">Showroom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Area (sqft)</Label><Input type="number" value={bulk.starting_area_sqft} onChange={(e) => setBulk({ ...bulk, starting_area_sqft: +e.target.value })} /></div>
                    <div className="col-span-2"><Label>Base Price (₹)</Label><Input type="number" value={bulk.starting_price} onChange={(e) => setBulk({ ...bulk, starting_price: +e.target.value })} /></div>
                  </div>
                  <DialogFooter>
                    <Button onClick={() => bulkMut.mutate({ ...bulk, project_id: id })} className="bg-emerald-700 hover:bg-emerald-800" data-testid="bulk-create-confirm">
                      Create {bulk.floors * bulk.units_per_floor} units
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {units.length ? (
                <Link to="/inventory" className="text-sm text-emerald-700 hover:underline">Open interactive Sales Board →</Link>
              ) : (
                <div className="text-sm text-zinc-500">No units yet. Use Bulk Create to spin up an entire tower in one click.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="finance" className="mt-4">
          <Card className="bg-white border-zinc-200">
            <CardHeader><CardTitle className="font-heading text-base">Project-wise P&amp;L</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(pnl || {}).map(([k, v]) => (
                <div key={k} className="border border-zinc-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k.replace(/_/g, " ")}</div>
                  <div className="font-mono font-semibold mt-1">{typeof v === "number" && k.includes("count") ? v : inr(v)}</div>
                </div>
              ))}
            </CardContent>
            <CardContent><Link to="/finance" className="text-sm text-emerald-700 hover:underline">Open full Finance ledger →</Link></CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="jda" className="mt-4 space-y-4">
          <Card className="bg-white border-zinc-200">
            <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Landmark size={16} /> Joint Development / Land Owners</CardTitle></CardHeader>
            <CardContent>
              {project.land_owners?.length ? (
                <div className="divide-y divide-zinc-200">
                  {project.land_owners.map((lo) => (
                    <div key={lo.id} className="py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div>
                        <div className="font-semibold">{lo.name}</div>
                        <div className="text-xs text-zinc-500">PAN {lo.pan || "—"} · Agreement till {fmtDate(lo.agreement_expiry)}</div>
                      </div>
                      <div className="text-xs font-mono">
                        <span className="mr-3">Model: <b className="uppercase">{lo.revenue_share_model}</b></span>
                        {lo.share_percent > 0 && <span className="mr-3">Share: {lo.share_percent}%</span>}
                        {lo.fixed_amount > 0 && <span className="mr-3">Fixed: {inr(lo.fixed_amount)}</span>}
                        {lo.unit_allocation_count > 0 && <span>Units: {lo.unit_allocation_count}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-zinc-500">
                  This project is <b>{project.ownership_type === "jda" ? "under JDA" : "company-owned"}</b>.
                  {project.ownership_type === "jda" && (
                    <> Add a land owner in <Link to="/land-owners" className="text-emerald-700 underline">Land Owners</Link>.</>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <AuditTrail entityType="project" entityId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProjectConfigCard({ project, unitStats }) {
  const cfg = [
    { k: "number_of_buildings", l: "Buildings" },
    { k: "number_of_towers", l: "Towers" },
    { k: "number_of_wings", l: "Wings" },
    { k: "total_floors", l: "Floors / tower" },
    { k: "number_of_flats", l: "Flats" },
    { k: "number_of_row_houses", l: "Row Houses" },
    { k: "number_of_shops", l: "Shops" },
    { k: "number_of_offices", l: "Offices" },
    { k: "number_of_showrooms", l: "Showrooms" },
  ];
  const planned =
    (project.number_of_flats || 0) +
    (project.number_of_row_houses || 0) +
    (project.number_of_shops || 0) +
    (project.number_of_offices || 0) +
    (project.number_of_showrooms || 0);
  const built = unitStats.total || 0;
  const hasAny = cfg.some((f) => Number(project[f.k] || 0) > 0);
  if (!hasAny) return null;
  return (
    <Card className="bg-white border-zinc-200" data-testid="project-config-card">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Building2 size={16} /> Project Configuration
        </CardTitle>
        <div className="text-[11px] font-mono text-zinc-500">
          Built <b className="text-zinc-800">{built}</b> of planned <b className="text-zinc-800">{planned}</b> units
          {planned > 0 && (
            <span className={`ml-2 uppercase tracking-widest px-1.5 py-0.5 rounded text-[9px] ${
              built >= planned
                ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                : "bg-amber-50 text-amber-800 border border-amber-300"
            }`}>
              {Math.min(100, Math.round((built / planned) * 100))}%
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
        {cfg.map((f) => (
          <div key={f.k} className="border border-zinc-200 rounded-md p-2 text-center">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{f.l}</div>
            <div className="font-mono font-bold text-lg mt-0.5">{project[f.k] || 0}</div>
          </div>
        ))}
      </CardContent>
      {project.unit_configuration && (
        <CardContent className="text-xs text-zinc-600 pt-0">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-2">Unit Mix:</span>
          {project.unit_configuration}
        </CardContent>
      )}
    </Card>
  );
}

function AuditTrail({ entityType, entityId }) {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["audit", entityType, entityId],
    queryFn: async () => (await api.get("/audit-logs", { params: { entity_type: entityType, entity_id: entityId, limit: 100 } })).data,
  });
  return (
    <Card className="bg-white border-zinc-200">
      <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><History size={16} /> Audit Trail</CardTitle></CardHeader>
      <CardContent>
        {isLoading ? <div className="text-sm text-zinc-500">Loading…</div> :
         !logs.length ? <div className="text-sm text-zinc-500">No changes recorded yet.</div> :
         <div className="divide-y divide-zinc-200" data-testid="audit-list">
           {logs.map((l) => (
             <div key={l.id} className="py-3">
               <div className="flex items-center justify-between text-xs">
                 <div>
                   <b className="text-zinc-800">{l.actor_name}</b>
                   <span className="text-zinc-500"> · {l.actor_role}</span>
                   <span className="ml-2 uppercase tracking-widest text-[10px] bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 rounded">{l.action}</span>
                 </div>
                 <div className="font-mono text-zinc-500">{fmtDate(l.at)}</div>
               </div>
               {l.changed_fields?.length > 0 && (
                 <div className="mt-2 text-xs space-y-1">
                   {l.changed_fields.map((k) => (
                     <div key={k} className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-x-3">
                       <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{k}</span>
                       <span>
                         <span className="text-red-600 line-through mr-2">{String(l.before?.[k] ?? "—")}</span>
                         <span className="text-emerald-700">{String(l.after?.[k] ?? "—")}</span>
                       </span>
                     </div>
                   ))}
                 </div>
               )}
               {l.reason && <div className="mt-1 text-[11px] italic text-zinc-500">Reason: {l.reason}</div>}
             </div>
           ))}
         </div>}
      </CardContent>
    </Card>
  );
}